// Skins runner. Pure function — given the round's course, players,
// scores, and configured options, returns two independent payout
// sections: one settled on raw gross scores, one on net scores
// (gross − strokes received). Gross and net are scored as parallel
// games and never share a pot.
//
// Per-hole rule (both modes, applied independently to gross and net):
//   • need at least two scored players to compete (pot mode) OR
//     every player must have a score on the hole (carry-forward)
//   • unique lowest score wins the hole
//   • ties on lowest = no winner this hole
//
// Pot mode end-of-round (per section):
//   • totalSkins = sum of every player's skins won for that section
//   • totalPot   = players.length × bet (perPlayer) OR the fixed total pot
//                  (totalPot mode) — a single round pot either way
//   • valuePerSkin = totalPot / totalSkins  (0 when no skins)
//   • winnings[p] = skinsWon[p] × valuePerSkin
//
// Carry-forward mode (per section):
//   • bet is taken from each player on EVERY hole; per-hole stake is
//     players.length × bet.
//   • A tied hole rolls its stake into the next hole's pot — the
//     winner of the next outright hole sweeps the accumulated stakes.
//   • An outright-won hole pays the running pot to that player and
//     resets the carry to zero.
//   • If the round ends with tied holes still in carry, the leftover
//     stake is reported as `unclaimedPot` so the UI can surface it.
//   • A hole only settles when every player has a score on it; until
//     then no stake is contributed and no settlement is computed.

import { Course, Hole, Player, ScoreMap } from "../types";

// WHS-style course handicap — mirrors the helper in stableford.ts so
// the runner stays standalone (no cross-runner imports).
function courseHandicap(
  handicapIndex: number | undefined,
  slope: number | undefined,
  rating: number | undefined,
  totalPar: number,
): number {
  if (handicapIndex == null) return 0;
  const slopeFactor = slope ? slope / 113 : 1;
  const ratingDiff = rating != null ? rating - totalPar : 0;
  return Math.round(handicapIndex * slopeFactor + ratingDiff);
}

function strokesOnHole(courseHcp: number, hole: Hole): number {
  if (courseHcp <= 0) return 0;
  const base = Math.floor(courseHcp / 18);
  const remainder = courseHcp % 18;
  return base + (hole.si <= remainder ? 1 : 0);
}

export interface SkinsOptions {
  mode?: "pot" | "carryForward";
  // Pot mode: how the pot is set. "perPlayer" (default) = each player antes the
  // per-player bet, so pot = players × bet. "totalPot" = a fixed total pot
  // regardless of player count (used by tournaments). Gross and net are
  // independent pots in both cases.
  potMode?: "perPlayer" | "totalPot";
  potBetGross?: number;
  potBetNet?: number;
  potTotalGross?: number;
  potTotalNet?: number;
  carryBetGross?: number;
  carryBetNet?: number;
  // Tournament: when true the pot/entry fee was collected up front. The runner
  // always returns GROSS winnings; the leaderboard reads this flag and skips
  // the per-player stake deduction so winners show the full pot. (Not consumed
  // by the runner itself.)
  entryFeePaid?: boolean;
}

export interface SkinsPayout {
  skinsWon: number;
  winnings: number;
  // 1-based hole numbers where this player won an outright skin in
  // this section. Surfaced for the leaderboard's click-to-expand
  // detail ("Holes: 3, 7, 12") below each row.
  holes: number[];
}

// One pot's worth of state. Gross and net each produce one of these.
export interface SkinsSection {
  payouts: Record<string, SkinsPayout>;
  totalSkins: number;
  // Pot-mode only — even cash value of one skin. 0 in carry-forward
  // mode (each pot is paid out as it's won).
  valuePerSkin: number;
  // Pot mode: the static round pot (players × bet, or the fixed total pot).
  // Carry-forward: the sum of all stakes contributed across played
  // holes — informational so the UI can sanity-check totalDistributed.
  totalPot: number;
  // Carry-forward only — stake left in the rolling pot at the end of
  // the round because the last contested holes tied. 0 in pot mode.
  unclaimedPot: number;
  // True when this section's bet is > 0. Leaderboard uses this to
  // decide whether to render the section's table at all.
  hasAnyBet: boolean;
}

export interface SkinsResult {
  gross: SkinsSection;
  net: SkinsSection;
  // "pot" or "carryForward" — surfaced so the leaderboard can label
  // the section and decide whether to show the unclaimed-pot footer.
  mode: "pot" | "carryForward";
  // Convenience aggregate — true when at least one section has a bet.
  hasAnyBet: boolean;
}

export interface SkinsInput {
  course: Course;
  players: Player[];
  scores: ScoreMap;
  options: SkinsOptions;
}

// Settle one Skins section given a per-(player, hole) score lookup.
// `scoreFn` returns null when the player has no score on the hole, so
// the section can support both gross (raw) and net (handicap-adjusted)
// without duplicating the loop body.
// `pot` is the full section pot already resolved by the caller — players ×
// per-player bet for "perPlayer" mode, or the fixed total for "totalPot" mode.
function settleSection(
  course: Course,
  players: Player[],
  pot: number,
  scoreFn: (playerId: string, hole: Hole) => number | null,
): SkinsSection {
  const skinsWon: Record<string, number> = {};
  const holesWon: Record<string, number[]> = {};
  for (const p of players) {
    skinsWon[p.id] = 0;
    holesWon[p.id] = [];
  }

  for (const hole of course.holes) {
    const playerScores: Array<{ id: string; score: number }> = [];
    for (const p of players) {
      const s = scoreFn(p.id, hole);
      if (s == null) continue;
      playerScores.push({ id: p.id, score: s });
    }
    // Need at least two scored players for a competitive hole.
    if (playerScores.length < 2) continue;
    const minScore = Math.min(...playerScores.map((s) => s.score));
    const winners = playerScores.filter((s) => s.score === minScore);
    if (winners.length === 1) {
      skinsWon[winners[0].id]++;
      holesWon[winners[0].id].push(hole.number);
    }
    // ties: no skin awarded (and in pot mode the unawarded skin just
    // doesn't exist — there's no carry to the next hole)
  }

  const totalSkins = Object.values(skinsWon).reduce((a, b) => a + b, 0);
  const totalPot = pot;
  const valuePerSkin = totalSkins > 0 ? totalPot / totalSkins : 0;

  // Gross winnings — winners split the pot. The leaderboard applies the
  // per-player stake (and the Entry-Fee-Paid rule) when it shows net position.
  const payouts: Record<string, SkinsPayout> = {};
  for (const p of players) {
    const won = skinsWon[p.id] || 0;
    payouts[p.id] = {
      skinsWon: won,
      winnings: won * valuePerSkin,
      holes: holesWon[p.id],
    };
  }

  return {
    payouts,
    totalSkins,
    valuePerSkin,
    totalPot,
    unclaimedPot: 0,
    hasAnyBet: pot > 0,
  };
}

// Carry-forward settlement. Each hole contributes (players.length ×
// bet) to a running pot; an outright winner takes the running pot and
// resets it, a tied hole rolls the pot forward. A hole is skipped
// entirely (no stake contribution) until every player has a score on
// it, so partial leaderboards mid-round display the carry sensibly.
function settleSectionCarry(
  course: Course,
  players: Player[],
  bet: number,
  scoreFn: (playerId: string, hole: Hole) => number | null,
): SkinsSection {
  const skinsWon: Record<string, number> = {};
  const holesWon: Record<string, number[]> = {};
  const winnings: Record<string, number> = {};
  for (const p of players) {
    skinsWon[p.id] = 0;
    holesWon[p.id] = [];
    winnings[p.id] = 0;
  }

  const perHoleStake = players.length * bet;
  let carryStake = 0;
  let totalContributed = 0;

  for (const hole of course.holes) {
    // Every player must have a score before the hole contributes to
    // the carry — partial holes sit out until completed.
    const playerScores: Array<{ id: string; score: number }> = [];
    let anyMissing = false;
    for (const p of players) {
      const s = scoreFn(p.id, hole);
      if (s == null) {
        anyMissing = true;
        break;
      }
      playerScores.push({ id: p.id, score: s });
    }
    if (anyMissing) continue;

    // Ante in for this hole.
    carryStake += perHoleStake;
    totalContributed += perHoleStake;

    const minScore = Math.min(...playerScores.map((s) => s.score));
    const winners = playerScores.filter((s) => s.score === minScore);
    if (winners.length === 1) {
      const w = winners[0].id;
      skinsWon[w]++;
      holesWon[w].push(hole.number);
      winnings[w] += carryStake;
      carryStake = 0;
    }
    // tie: carry stake into the next hole — nothing else to do.
  }

  const payouts: Record<string, SkinsPayout> = {};
  for (const p of players) {
    payouts[p.id] = {
      skinsWon: skinsWon[p.id],
      winnings: winnings[p.id],
      holes: holesWon[p.id],
    };
  }

  const totalSkins = Object.values(skinsWon).reduce((a, b) => a + b, 0);

  return {
    payouts,
    totalSkins,
    // Pot value-per-skin doesn't apply — each pot pays out at its
    // own size as it's won, so we leave this at 0 to match.
    valuePerSkin: 0,
    totalPot: totalContributed,
    unclaimedPot: carryStake,
    hasAnyBet: bet > 0,
  };
}

export function runSkins(input: SkinsInput): SkinsResult {
  const { course, players, scores, options } = input;
  const mode: "pot" | "carryForward" =
    options.mode === "carryForward" ? "carryForward" : "pot";

  // Course handicap cache used only by the net section. Computed once
  // up front so the per-hole loop doesn't repeat the slope/rating math.
  const totalPar = course.holes.reduce((s, h) => s + h.par, 0);
  const tees = course.tees || [];
  const courseHcpById: Record<string, number> = {};
  for (const p of players) {
    const tee = tees.find((t) => t.id === p.tee);
    courseHcpById[p.id] = courseHandicap(
      p.handicap,
      tee?.slope,
      tee?.rating,
      totalPar,
    );
  }

  const grossScoreFn = (playerId: string, hole: Hole): number | null => {
    const sc = (scores[playerId] || {})[hole.number];
    if (!sc || typeof sc.gross !== "number") return null;
    return sc.gross;
  };

  const netScoreFn = (playerId: string, hole: Hole): number | null => {
    const sc = (scores[playerId] || {})[hole.number];
    if (!sc || typeof sc.gross !== "number") return null;
    return sc.gross - strokesOnHole(courseHcpById[playerId], hole);
  };

  let gross: SkinsSection;
  let net: SkinsSection;
  if (mode === "carryForward") {
    // Carry-forward antes the per-hole bet each hole.
    const grossBet = Number(options.carryBetGross) || 0;
    const netBet = Number(options.carryBetNet) || 0;
    gross = settleSectionCarry(course, players, grossBet, grossScoreFn);
    net = settleSectionCarry(course, players, netBet, netScoreFn);
  } else {
    // Pot mode: resolve the full pot per section. "totalPot" uses the fixed
    // amounts (tournaments); "perPlayer" multiplies the per-player bet by the
    // player count.
    const totalPotMode = options.potMode === "totalPot";
    const grossPot = totalPotMode
      ? Number(options.potTotalGross) || 0
      : players.length * (Number(options.potBetGross) || 0);
    const netPot = totalPotMode
      ? Number(options.potTotalNet) || 0
      : players.length * (Number(options.potBetNet) || 0);
    gross = settleSection(course, players, grossPot, grossScoreFn);
    net = settleSection(course, players, netPot, netScoreFn);
  }

  return {
    gross,
    net,
    mode,
    hasAnyBet: gross.hasAnyBet || net.hasAnyBet,
  };
}
